import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

const PROCOUNTOR_URL = 'https://secure.procountor.com/procountor/?l=2&f=0';

const Code: React.FC = ({ children }) => (
    <Box
        component="code"
        sx={{
            fontFamily: 'monospace',
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            borderRadius: 0.5,
            padding: '1px 5px',
            fontSize: '0.9em',
            wordBreak: 'break-word'
        }}
    >
        {children}
    </Box>
);

const Step: React.FC<{ title: string }> = ({ title, children }) => (
    <Box component="li" sx={{ marginBottom: 2 }}>
        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
            {title}
        </Typography>
        <Box sx={{ marginTop: 0.5 }}>{children}</Box>
    </Box>
);

const HowToInvoiceComponentPage: React.FC = () => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 900 }}>
            <Box>
                <Typography variant="h5">Procountor Invoicing &amp; Monthly Reporting Manual</Typography>
            </Box>

            <Paper sx={{ padding: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Part 1: Preparing and Exporting Invoices
                </Typography>
                <Box component="ol" sx={{ paddingLeft: 3, margin: 0 }}>
                    <Step title="Initialize the Invoice Page">
                        <Typography variant="body2">
                            Prerequisite: ensure all necessary records for the period are updated. Open or refresh
                            the invoicing page. The current date should be automatically pre-selected as the{' '}
                            <strong>Invoice Date</strong>.
                        </Typography>
                    </Step>
                    <Step title="Resolve Missing Procountor IDs">
                        <Typography variant="body2" gutterBottom>
                            Look for orange rows. If any rows are highlighted in <strong>orange</strong>, they are
                            missing a <Code>ProcountorId</Code>. Fix them using these steps:
                        </Typography>
                        <Box component="ul" sx={{ paddingLeft: 3, margin: 0 }}>
                            <Typography component="li" variant="body2">
                                Click the <strong>Generate ProcountorId</strong> button and copy the generated
                                number to your clipboard.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Click the <strong>Broker Firm name</strong> (located directly below the Broker
                                Agent&apos;s name) to open the <em>Edit Broker Firm</em> dialog.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Navigate to the <strong>Integration</strong> tab, paste the copied ID into the{' '}
                                <strong>Procountor Id</strong> field, and click <strong>Save</strong>, then{' '}
                                <strong>Close</strong>.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Scroll to the top of the page and click <strong>Refresh Preview</strong>.
                            </Typography>
                        </Box>
                        <Alert severity="success" sx={{ marginTop: 1 }}>
                            <strong>Verification:</strong> If done correctly, the rows for that Broker Agent and
                            Broker Firm will turn <strong>green</strong>.
                        </Alert>
                    </Step>
                    <Step title="Export the CSV File">
                        <Typography variant="body2" gutterBottom>
                            When all records are <strong>green</strong>:
                        </Typography>
                        <Box component="ul" sx={{ paddingLeft: 3, margin: 0 }}>
                            <Typography component="li" variant="body2">
                                Check the <strong>Select visible rows</strong> checkbox. This activates the{' '}
                                <strong>Export To CSV</strong> button.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Click <strong>Export To CSV</strong>. Your browser will download a file named{' '}
                                <Code>invoices_YYYY-MM_DD_HHmmss.csv</Code>.
                            </Typography>
                        </Box>
                    </Step>
                    <Step title="Mark the Invoices as Completed">
                        <Typography variant="body2" gutterBottom>
                            Do this <strong>only after</strong> the CSV file has been imported into Procountor
                            successfully (see Part 2). It tells the system that these invoices are done, so they
                            are not exported again next time.
                        </Typography>
                        <Box component="ul" sx={{ paddingLeft: 3, margin: 0 }}>
                            <Typography component="li" variant="body2">
                                Go back to the <strong>Export to Procountor</strong> tab.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Select the same rows you exported. The easiest way: set the{' '}
                                <strong>Status</strong> filter to <strong>Exported</strong> and then check the{' '}
                                <strong>Select visible rows</strong> checkbox.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Click the <strong>Mark As Completed</strong> button.
                            </Typography>
                            <Typography component="li" variant="body2">
                                The status of the selected rows changes to <strong>Completed</strong>.
                            </Typography>
                        </Box>
                        <Alert severity="warning" sx={{ marginTop: 1 }}>
                            <strong>Important:</strong> Do not click <strong>Mark As Completed</strong> if the
                            import in Procountor failed or you cancelled it. In that case use{' '}
                            <strong>Rollback Exported</strong> instead, fix the problem, and export again.
                        </Alert>
                    </Step>
                </Box>
            </Paper>

            <Paper sx={{ padding: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Part 2: Importing into Procountor
                </Typography>
                <Box component="ol" sx={{ paddingLeft: 3, margin: 0 }}>
                    <Step title="Log In to Procountor">
                        <Box component="ul" sx={{ paddingLeft: 3, margin: 0 }}>
                            <Typography component="li" variant="body2">
                                Navigate to:{' '}
                                <Link href={PROCOUNTOR_URL} target="_blank" rel="noopener noreferrer">
                                    {PROCOUNTOR_URL}
                                </Link>
                            </Typography>
                            <Typography component="li" variant="body2">
                                Select your language, check <strong>Remember me</strong>, enter your credentials,
                                and click <strong>Next</strong>.
                            </Typography>
                        </Box>
                    </Step>
                    <Step title="Navigate to Data Import">
                        <Typography variant="body2">
                            On the left-hand menu, go to <strong>Management</strong> &gt;{' '}
                            <strong>Import data</strong>.
                        </Typography>
                    </Step>
                    <Step title="Upload and Validate the CSV">
                        <Box component="ul" sx={{ paddingLeft: 3, margin: 0 }}>
                            <Typography component="li" variant="body2">
                                In the <strong>Import type</strong> dropdown, select{' '}
                                <strong>Import invoices (CSV, DDE, ZIP)</strong>.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Upload your exported <Code>invoices_YYYY-MM_DD_HHmmss.csv</Code> file, then click{' '}
                                <strong>Continue</strong>, and click <strong>Continue</strong> again.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Scroll down and check the list for errors.
                            </Typography>
                        </Box>
                    </Step>
                    <Step title="Verify Import Results">
                        <Typography variant="body2" gutterBottom>
                            Crucial validation step. Check the <strong>Import result</strong> message above the
                            list. It must read:
                        </Typography>
                        <Alert severity="info" sx={{ marginBottom: 1 }}>
                            Amount of imported lines &#123;XXX&#125;. Amount of correctly read rows: &#123;XXX&#125;.
                            Amount of incorrectly read rows: 0.
                        </Alert>
                        <Typography variant="body2" sx={{ fontWeight: 600, marginTop: 1 }}>
                            If incorrectly read rows is NOT 0 (red lines visible):
                        </Typography>
                        <Box component="ol" sx={{ paddingLeft: 3, margin: 0 }}>
                            <Typography component="li" variant="body2">
                                Copy the number from the <strong>Customer No.</strong> column.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Find the corresponding Broker Firm on the invoicing app page.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Send this information to Robert so he can add the new customer to Procountor.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Click <strong>Cancel</strong> in Procountor to avoid accidentally approving an
                                incorrect list.
                            </Typography>
                            <Typography component="li" variant="body2">
                                Repeat the export/import process once Robert updates the system.
                            </Typography>
                        </Box>
                        <Alert severity="warning" sx={{ marginTop: 1 }}>
                            <strong>Important:</strong> Do not approve a list that still contains red lines.
                        </Alert>
                        <Typography variant="body2" sx={{ fontWeight: 600, marginTop: 2 }}>
                            If incorrectly read rows IS 0 (zero red lines):
                        </Typography>
                        <Typography variant="body2">
                            Click <strong>Save</strong> to finalize the import.
                        </Typography>
                    </Step>
                </Box>
            </Paper>

            <Paper sx={{ padding: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Part 3: Beginning-of-Month Linear Reporting
                </Typography>
                <Typography variant="body2" gutterBottom>
                    When invoicing at the beginning of a month, you must also provide a combined report for Linear
                    covering the <strong>previous two invoicing runs</strong> (the mid-month invoice from the
                    previous month and the current start-of-month invoice).
                </Typography>
                <Divider sx={{ marginY: 1 }} />
                <Typography variant="body2">
                    <strong>Action:</strong> Navigate to the <strong>Reports</strong> tab on the invoicing platform
                    to generate this data.
                </Typography>
            </Paper>
        </Box>
    );
};

export default HowToInvoiceComponentPage;
